import Loading from '@/components/loading/skeleton';
import { percentage } from '@/hooks/useGetPosition';
import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { useAddress } from '@/store/selector';
import { TradeData } from '@/types/trade';
import { closePosition } from '@/utils/close-position';
import { formatDisplay } from '@/utils/format-display';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SheetDetail from './sheet-detail';
import { useGetFees } from './useGetFees';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Spinner } from '@/components/spinner';
import { MiniKit } from '@worldcoin/minikit-js';
import { toastApp } from '@/components/toast-message/sonner';
import { useTradeStore } from '@/store/trade-store';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { formatMiniAppLink } from '@/utils/mini-app-link';
dayjs.extend(utc);

type Props = {
    item: TradeData;
    index: number;
};

export default function Item({ item, index }: Props) {
    const [isSubmit, setIsSubmit] = useState(false);
    const { t } = useTranslation();
    const { isClose } = useCheckClosePair(Number(item.pair));
    const [showCashOut, setShowCashOut] = useState<number | null>(null);
    const removeOrderByIndex = useTradeStore((state) => {
        return state.removeOrderByIndex;
    });
    const address = useAddress();

    const [isOpenDetail, setIsOpenDetail] = useState(false);

    const disabled = isSubmit || isClose;

    const { data } = useGetFees(
        address,
        item.indexPrice,
        item.index,
        item.createdAt,
        item.buy,
        item.initialPosToken,
        item.leverage
    );

    const earn = useMemo(() => {
        return (
            item.net_pnl.earn -
            (Number(data?.fundingFee) || 0) -
            (Number(data?.rolloverFee) || 0)
        );
    }, [data?.fundingFee, data?.rolloverFee, item.net_pnl.earn]);

    const pnl = useMemo(() => {
        const value = (earn / item.collateralValue) * 100;
        return Math.max(value, -100);
    }, [earn, item.collateralValue]);

    const handleToggleDetail = useCallback(() => {
        setIsOpenDetail((prev) => !prev);
    }, []);

    const onProfitableClose = useCallback((earn: number) => {
        // TODO: implement
        setShowCashOut(earn);
    }, []);

    const handleClose = useCallback(async () => {
        try {
            if (!MiniKit.isInstalled()) {
                toastApp.error('Minikit not install');
                return;
            }
            setIsSubmit(true);
            const txHash = await closePosition(item.pair, item.index, address);
            if (txHash) {
                removeOrderByIndex(index);
                if (earn > 0.5) {
                    onProfitableClose(earn);
                }
            }
        } finally {
            setIsSubmit(false);
        }
    }, [address, earn, index, item, onProfitableClose, removeOrderByIndex]);

    return (
        <>
            <div
                onClick={() => {
                    handleToggleDetail();
                }}
                className="bg-white rounded-2xl p-4 shadow-card-light  border border-[#E6E8EC] transition-all"
            >
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="relative w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center border border-[#E6E8EC] shrink-0">
                            <img
                                className="w-full h-full object-cover"
                                src={item.imgUrl}
                                alt={item.pairName}
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <div className="font-bold text-base tracking-tight leading-tight">
                                    {item.pairName}
                                </div>
                                <div className="flex gap-1">
                                    <span
                                        className={clsx(
                                            'text-[10px] font-bold border px-1 py-px rounded  leading-none uppercase',
                                            {
                                                'text-[#00C076] border-[#00C076]/30 bg-[#00C076]/10':
                                                    item.type == 'Long',
                                                'text-[#FF4D4F] border-[#FF4D4F]/30 bg-[#FF4D4F]/10':
                                                    item.type !== 'Long',
                                            }
                                        )}
                                    >
                                        {item.type}
                                    </span>
                                    <span className="text-[10px] font-bold text-[#6F767E] border border-[#E6E8EC]  px-1 py-px rounded bg-gray-100 leading-none">
                                        {item.leverage}x
                                    </span>
                                </div>
                            </div>
                            <div className="text-[10px] text-[#6F767E] font-medium">
                                {t('Open At')}{' '}
                                {dayjs
                                    .utc(Number(item.createdAt) * 1000)
                                    .format('DD/MM/YYYY - HH:mm:ss')}
                            </div>
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="text-[10px] text-[#6F767E] font-medium mb-0.5">
                            {t('Amount')}
                        </div>
                        <div className="text-sm font-bold text-[#1A1D1F] ">
                            {formatDisplay(item.collateralValue, {
                                decimalToShow: 2,
                            })}{' '}
                            {item.tradeCoinName}
                        </div>
                    </div>
                </div>
                <Loading
                    isLoading={item.isLoading}
                    className="min-h-[72px] mt-3"
                >
                    <div
                        className={clsx(
                            'border border-dashed rounded-xl py-3 px-4 mb-5 flex justify-between items-center',
                            {
                                'border-[#FF4D4F]/30 bg-[#FF4D4F]/5 ': earn < 0,
                                'border-[#00C076]/30 bg-[#00C076]/5 ':
                                    earn >= 0,
                            }
                        )}
                    >
                        <div>
                            <div className="text-xs font-medium text-[#6F767E] mb-0.5">
                                PNL
                            </div>
                            <div
                                className={clsx('text-xl font-bold ', {
                                    'text-[#FF4D4F]': earn < 0,
                                    'text-[#00C076]': earn >= 0,
                                })}
                            >
                                {earn > 0 ? '+' : ''}
                                {formatDisplay(earn, {
                                    decimalToShow: 2,
                                })}{' '}
                                {item.tradeCoinName}
                            </div>
                        </div>
                        <div className="text-right">
                            <div
                                className={clsx('text-xs font-medium  mb-0.5', {
                                    'text-[#FF4D4F]/80': earn < 0,
                                    'text-[#00C076]/80': earn >= 0,
                                })}
                            >
                                % ROI
                            </div>
                            <div
                                className={clsx('text-lg font-bold ', {
                                    'text-[#FF4D4F]': earn < 0,
                                    'text-[#00C076]': earn >= 0,
                                })}
                            >
                                {percentage(pnl)}
                            </div>
                        </div>
                    </div>
                </Loading>
                <div className="grid grid-cols-3 gap-2 mb-5 divide-x divide-[#E6E8EC] ">
                    <div className="text-center px-0.5">
                        <div className="text-[10px] text-[#6F767E] mb-1 uppercase tracking-wide truncate">
                            {t('Entry Price')}
                        </div>
                        <div className="text-xs sm:text-sm font-semibold  text-[#1A1D1F] ">
                            {formatDisplay(Number(item.open_price), {
                                decimalToShow: item.decimalToShow,
                            })}
                        </div>
                    </div>
                    <div className="text-center px-0.5">
                        <div className="text-[10px] text-[#6F767E] mb-1 uppercase tracking-wide truncate">
                            {t('Current Price')}
                        </div>
                        <div className="text-xs sm:text-sm font-semibold  text-[#1A1D1F] ">
                            {formatDisplay(Number(item.open_price), {
                                decimalToShow: item.decimalToShow,
                            })}
                        </div>
                    </div>
                    <div className="text-center px-0.5">
                        <div className="text-[10px] text-[#6F767E] mb-1 uppercase tracking-wide truncate">
                            {t('Liq. Price')}
                        </div>
                        <div className="text-xs sm:text-sm font-semibold  text-[#1A1D1F] ">
                            {formatDisplay(item.liquidationPrice, {
                                decimalToShow: item.decimalToShow,
                            })}{' '}
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <button
                        disabled={disabled}
                        className="flex items-center justify-center bg-gray-100 text-[#1A1D1F]  font-semibold py-3 rounded-xl text-sm hover:bg-gray-200  transition-colors disabled:opacity-50"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClose();
                        }}
                    >
                        {isSubmit ? (
                            <Spinner className="w-4 h-4" />
                        ) : (
                            <p className="text-center"> {t('Close')}</p>
                        )}
                    </button>
                    <button
                        className="bg-gray-100 text-[#1A1D1F]  font-semibold py-3 rounded-xl text-sm hover:bg-gray-200 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleDetail();
                        }}
                    >
                        {t('View Detail')}
                    </button>
                </div>
            </div>
            <SheetDetail
                item={item}
                isOpen={isOpenDetail}
                onClose={handleToggleDetail}
            />

            {showCashOut
                ? createPortal(
                      <div
                          onClick={() => {
                              setShowCashOut(null);
                          }}
                          className="fixed inset-0 z-999 bg-black/70 p-4 flex items-center justify-center"
                      >
                          <Link
                              className="relative max-w-md"
                              to={formatMiniAppLink("https://worldcoin.org/mini-app/?app_id=app_0d4b759921490adc1f2bd569fda9b53a&path=%2Fpay%2Fscan%3Fintro%3D1")}
                              target="_blank"
                              onClick={() => {
                                  setShowCashOut(null);
                              }}
                          >
                              <img
                                  src="/images/bg_cashout.png"
                                  className="w-full max-w-md"
                              />
                              <div className="absolute top-8 left-4 right-4 flex flex-col items-center">
                                  <p className="text-center text-2xl text-black font-bold">
                                      {t('Cash out WLD to your Bank')}
                                  </p>
                                  <p className="text-center text-base text-black">
                                      {t('And get 50% Cashback')}
                                  </p>
                              </div>

                              <div className="gap-4 flex-col absolute left-4 right-4 bottom-4 flex items-center justify-center">
                                  <p className="text-center text-4xl font-semibold text-[#34C759]">
                                      +
                                      {formatDisplay(showCashOut, {
                                          decimalToShow: 2,
                                      })}{' '}
                                      WLD
                                  </p>
                                  <p className="text-center text-[#808080] text-xs">
                                      {t(
                                          'More cash out claim WLD, gift vouchers, free token'
                                      )}
                                  </p>
                                  <div className="w-full flex items-center justify-center cursor-pointer active:scale-95 transition-all">
                                      <img
                                          src="/images/btn_continute.png"
                                          className="w-full h-auto object-cover"
                                      />
                                      <div className="absolute flex items-center justify-center">
                                          <p className="font-thorn text-xl text-white">
                                              {t('Cash out Now')}
                                          </p>
                                      </div>
                                  </div>
                              </div>
                          </Link>
                      </div>,
                      document.body
                  )
                : null}
        </>
    );
}
